import { useState } from 'react'
import PropTypes from 'prop-types'
import { useLazyQuery, useMutation } from '@apollo/react-hooks'
import * as Yup from 'yup'

import Form from '~/components/shared/Form'
import { SignUp, RequestEmailVerificationCode, GetCurrentUser } from '~/graphql/Users.gql'
import { useToast } from '~/contexts/ToastProvider'
import withLocale from '~/hocs/locale'
import { useCurrentOrganization } from '~/utils/organization'
import * as auth from '~/utils/auth'

const INITIAL_VALUES = {
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  passwordConfirmation: '',
}

const VALIDATION = Yup.object({
  email: Yup.string().trim().email().required()
    .label('email'),
  firstName: Yup.string().trim().label('firstName').required(),
  lastName: Yup.string().trim().label('lastName').required(),
  password: Yup.string().trim().required().label('password'),
  passwordConfirmation: Yup.string().oneOf([Yup.ref('password'), null]),
})

const SignUpForm = ({
  email,
  passwordLabel,
  passwordConfirmationLabel,
  firstNameLabel,
  lastNameLabel,
  ctaLabel,
  ctaLoadingLabel,
  invalidUsernamePasswordLabel,
  errors,
  onSubmit,
}) => {
  const handleOnGetUser = data => {
    auth.storeCurrentUser(data.currentUser)
    addToast('success', 'signup', 'signup.success')
    onSubmit()
  }

  const handleOnEmailVerificationRequest = (data) => {
    const jwt = data?.authenticate?.jwt

    if (jwt) {
      auth.storeToken(jwt)
      getCurrentUser()
    }
  }

  const handleOnError = error => {
    auth.clearToken()

    const errorMessage = error.message.includes('hiago_users_email_unique')
      ? errors.emailAlreadyExists
      : errors.default

    setResponse({ error: new Error(errorMessage) })
  }

  const handleSubmit = async (values, { setErrors }) => {
    const variables = {
      email: values.email,
      password: values.password,
      firstName: values.firstName,
      lastName: values.lastName,
      organizationId: organization.id,
    }

    const response = await signUp({ variables })

    if (response) {
      await requestEmailVerificationCode({
        variables: {
          email: values.email,
          password: values.password,
        }
      })
    } else {
      setErrors({
        email: invalidUsernamePasswordLabel,
        password: invalidUsernamePasswordLabel,
      })
    }
  }

  const [signUp] = useMutation(SignUp, { onError: handleOnError })
  const [requestEmailVerificationCode] = useMutation(RequestEmailVerificationCode, {
    onError: handleOnError,
    onCompleted: handleOnEmailVerificationRequest,
  })
  const [organization] = useCurrentOrganization()
  const [getCurrentUser] = useLazyQuery(GetCurrentUser, {
    onCompleted: handleOnGetUser,
    onError: handleOnError,
    fetchPolicy: 'no-cache',
  })
  const [response, setResponse] = useState()
  const { addToast } = useToast()

  return (
    <Form
      onSubmit={handleSubmit}
      response={response}
      initialValues={INITIAL_VALUES}
      validationSchema={VALIDATION}
      validateOnChange={false}
      validateOnBlur={false}
    >
      <Form.Field name="email" label={email} />
      <Form.Field name="firstName" label={firstNameLabel} />
      <Form.Field name="lastName" label={lastNameLabel} />
      <Form.Field name="password" type="password" label={passwordLabel} />
      <Form.Field name="passwordConfirmation" type="password" label={passwordConfirmationLabel} />
      <div className="text-center">
        <Form.Submit
          text={ctaLabel}
          submittingText={ctaLoadingLabel}
          size="lg"
          className="mt-3"
          type="submit"
        />
      </div>
    </Form>
  )
}

SignUpForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  passwordLabel: PropTypes.string.isRequired,
  passwordConfirmationLabel: PropTypes.string.isRequired,
  firstNameLabel: PropTypes.string.isRequired,
  lastNameLabel: PropTypes.string.isRequired,
  ctaLabel: PropTypes.string.isRequired,
  ctaLoadingLabel: PropTypes.string.isRequired,
  invalidUsernamePasswordLabel: PropTypes.string.isRequired,
  errors: PropTypes.shape({
    emailAlreadyExists: PropTypes.string.isRequired,
    default: PropTypes.string.isRequired,
  }).isRequired,
}

export default withLocale('signup')(SignUpForm)
